// Onsite Registration — admin list view used at the event venue.
// Shows paginated athletes/caddies table with search and sort. Each row displays registration status,
// signature status (clickable to view/clear), and per-item checklist icons (directly toggleable).
// Action column opens the full registration form in a new tab or the checklist modal.
// Checklist icon clicks optimistically update state and PATCH the server; revert on failure.
// Row click navigates to the participant edit page (view-only mode).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faBook, faCamera, faMicrophone, faFlag, faGift, faScroll } from '@fortawesome/free-solid-svg-icons';
import API from '../../api/axiosInstance';
import OnsiteChecklistModal from './onsite/OnsiteChecklistModal';
import OnsiteSignatureModal from './onsite/OnsiteSignatureModal';
import '../css/OnsiteRegistration.css';

const ATHLETE_CHECKLIST = [
  { key: 'yardbookHandedOver',        icon: faBook,       label: 'Yardbook handed over' },
  { key: 'headshotTaken',             icon: faCamera,     label: 'Headshot taken' },
  { key: 'audioPronunciationCreated', icon: faMicrophone, label: 'Audio pronunciation created' },
  { key: 'flagSignatureMade',         icon: faFlag,       label: 'Flag signature made' },
  { key: 'giftHandedOver',            icon: faGift,       label: 'Gift handed over' },
];

const CADDIE_CHECKLIST = [
  { key: 'yardbookHandedOver',      icon: faBook,   label: 'Yardbook handed over' },
  { key: 'regulationsAcknowledged', icon: faScroll, label: 'I have read the Regulations' },
];

const BADGE_CLASS = {
  Registered: 'onsite-list__badge--registered',
  Approved:   'onsite-list__badge--approved',
  Pending:    'onsite-list__badge--pending',
  Rejected:   'onsite-list__badge--rejected',
  Added:      'onsite-list__badge--added',
};

const OnsiteRegistration = () => {

  //STATES USED IN THE COMPONENT
  const [view,        setView]        = useState('athletes');
  const [search,      setSearch]      = useState('');
  const [participants, setParticipants] = useState([]);
  const [total,       setTotal]       = useState(0);
  const [pages,       setPages]       = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [sortConfig,  setSortConfig]  = useState({ key: '', dir: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [checklist, setChecklist] = useState({ show: false, participant: null });
  const [sigModal,  setSigModal]  = useState({ show: false, participant: null });
  const [savingKeys, setSavingKeys] = useState(new Set());
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  //TYPE IS USED TO DETERMINE THE TYPE OF PARTICIPANT (ATHLETE OR CADDIE)
  const type = view === 'caddies' ? 'caddie' : 'athlete';

  //FETCH PARTICIPANTS IS USED TO FETCH THE PARTICIPANTS FROM THE SERVER
  const fetchParticipants = useCallback(async ({ page, limit, searchVal, sortKey, sortDir, participantType }) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        type:    participantType,
        page,
        limit,
        ...(searchVal  && { search:  searchVal }),
        ...(sortKey    && { sortKey, sortDir }),
      };
      const res = await API.get('/ems/onsite/participants', { params });
      const participants = res.data.data || [];
      setTotal(res.data.total ?? 0);
      setPages(res.data.pages ?? 1);

      if (participants.length > 0) {
        const ids = participants.map(p => p._id).join(',');
        const clRes = await API.get('/ems/onsite/checklists', { params: { ids } });
        const clMap = Object.fromEntries(
          (clRes.data.data || []).map(c => [c._id, c.onsiteChecklist])
        );
        setParticipants(participants.map(p => ({
          ...p,
          person_data: { ...p.person_data, onsiteChecklist: clMap[p._id] || {} },
        })));
      } else {
        setParticipants([]);
      }
    } catch (err) {
      setError(`Failed to load: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  //USE EFFECT IS USED TO FETCH THE PARTICIPANTS FROM THE SERVER
  useEffect(() => {
    fetchParticipants({
      page:            currentPage,
      limit:           itemsPerPage,
      searchVal:       search,
      sortKey:         sortConfig.key,
      sortDir:         sortConfig.dir,
      participantType: type,
    });
  }, [currentPage, itemsPerPage, sortConfig, type, fetchParticipants]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setCurrentPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchParticipants({
        page:            1,
        limit:           itemsPerPage,
        searchVal:       query,
        sortKey:         sortConfig.key,
        sortDir:         sortConfig.dir,
        participantType: type,
      });
    }, 300);
  };

  const handleViewChange = (newView) => {
    setView(newView);
    setCurrentPage(1);
    setSearch('');
    setSortConfig({ key: '', dir: 'asc' });
  };

  const toggleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
    setCurrentPage(1);
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.dir === 'asc' ? faSortUp : faSortDown;
  };

  const openForm = (p) =>
    window.open(`/onsite-registration-form?id=${p._id}&type=${type}`, '_blank');

  const handleChecklistUpdated = useCallback((id, newChecklist) => {
    setParticipants(prev => prev.map(p =>
      p._id === id ? { ...p, person_data: { ...p.person_data, onsiteChecklist: newChecklist } } : p
    ));
  }, []);

  const handleSignatureCleared = useCallback((id) => {
    setParticipants(prev => prev.map(p =>
      p._id === id ? { ...p, person_data: { ...p.person_data, signatureDataUrl: '' } } : p
    ));
  }, []);

  const handleIconToggle = useCallback(async (e, participant, itemKey) => {
    e.stopPropagation();
    const saveKey = `${participant._id}_${itemKey}`;
    if (savingKeys.has(saveKey)) return;
    const prev = participant.person_data?.onsiteChecklist || {};
    const next = { ...prev, [itemKey]: !prev[itemKey] };
    setParticipants(ps => ps.map(p =>
      p._id === participant._id ? { ...p, person_data: { ...p.person_data, onsiteChecklist: next } } : p
    ));
    setSavingKeys(s => new Set([...s, saveKey]));
    try {
      await API.patch(`/ems/onsite/participants/${participant._id}/checklist`, { onsiteChecklist: next });
    } catch {
      setParticipants(ps => ps.map(p =>
        p._id === participant._id ? { ...p, person_data: { ...p.person_data, onsiteChecklist: prev } } : p
      ));
    } finally {
      setSavingKeys(s => { const n = new Set(s); n.delete(saveKey); return n; });
    }
  }, [savingKeys]);

  const pageNumbers = Array.from({ length: pages }, (_, i) => i + 1);

  const SortHeader = ({ label, sortKey, center }) => (
    <div
      className={`sort-header ${sortConfig.key === sortKey ? 'sorted' : ''}`}
      style={center ? { justifyContent: 'center' } : undefined}
      onClick={() => toggleSort(sortKey)}
    >
      <span>{label}</span>
      <FontAwesomeIcon icon={sortIcon(sortKey)} className="sort-icon" />
    </div>
  );

  return (
    <div className="onsite-registration onsite-registration--list">

      {/* Toggle — outside table container */}
      <div className="onsite-list__toggle">
        <button
          className={`onsite-list__toggle-btn ${view === 'athletes' ? 'onsite-list__toggle-btn--active' : ''}`}
          onClick={() => handleViewChange('athletes')}
        >
          <i className="fa-solid fa-person-running" style={{ marginRight: 8 }} />Athletes
        </button>
        <button
          className={`onsite-list__toggle-btn ${view === 'caddies' ? 'onsite-list__toggle-btn--active' : ''}`}
          onClick={() => handleViewChange('caddies')}
        >
          <i className="fa-solid fa-bag-shopping" style={{ marginRight: 8 }} />Caddies
        </button>
      </div>

      <div className="onsite-list__container">

        {/* Search */}
        <div className="onsite-list__toolbar">
          <input
            className="onsite-list__search"
            type="search"
            placeholder={`Search ${view}…`}
            value={search}
            onChange={handleSearchChange}
            disabled={loading}
          />
        </div>

        {error && <div style={{ padding: '0 0 12px' }}><Alert variant="danger">{error}</Alert></div>}

        {/* Table */}
        <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
          <table className="onsite-list__table">
            <thead>
              <tr className="onsite-list__header-row">
                <th><SortHeader label="Last Name"  sortKey="lastname"  /></th>
                <th><SortHeader label="First Name" sortKey="firstname" /></th>
                {view === 'athletes' && <th style={{ textAlign: 'center' }}><SortHeader label="Status" sortKey="status" center /></th>}
                <th style={{ textAlign: 'center' }}>Signed</th>
                <th style={{ textAlign: 'center' }}>Checklist</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={view === 'athletes' ? 6 : 5} className="onsite-list__empty-cell">
                    <Spinner animation="border" size="sm" style={{ color: '#a67c2c', marginRight: 8 }} />
                    Loading…
                  </td>
                </tr>
              ) : participants.length === 0 ? (
                <tr>
                  <td colSpan={view === 'athletes' ? 6 : 5} className="onsite-list__empty-cell">
                    <i className="fa-solid fa-inbox" style={{ fontSize: 22, opacity: 0.3, marginRight: 8 }} />
                    No {view} found.
                  </td>
                </tr>
              ) : (
                participants.map((p) => {
                  const lastName  = p.person_data?.familyLastName || p.person_id?.lname || '';
                  const firstName = p.person_data?.givenFirstName  || p.person_id?.name  || '';
                  const status    = p.registration_status || 'Added';
                  const badgeCls  = BADGE_CLASS[status] || 'onsite-list__badge--added';
                  const signed    = !!p.person_data?.signatureDataUrl;
                  return (
                    <tr key={p._id} className="onsite-list__row" onClick={() => navigate(`/ems/participants/edit-participant/${p._id}?view=true`)}>
                      <td>{lastName}</td>
                      <td>{firstName}</td>
                      {view === 'athletes' && (
                        <td style={{ textAlign: 'center' }}>
                          <span className={`onsite-list__badge ${badgeCls}`}>{status}</span>
                        </td>
                      )}
                      <td style={{ textAlign: 'center' }}>
                        {signed
                          ? <button onClick={(e) => { e.stopPropagation(); setSigModal({ show: true, participant: p }); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                              <i className="fa-solid fa-circle-check" style={{ color: '#2e7d32', fontSize: '18px' }} />
                            </button>
                          : <span className="onsite-list__signed-icon" data-tooltip="Not signed yet">
                              <i className="fa-solid fa-circle-xmark" style={{ color: '#b32424', fontSize: '18px' }} />
                            </span>
                        }
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                          {(type === 'caddie' ? CADDIE_CHECKLIST : ATHLETE_CHECKLIST).map(item => {
                            const done = !!p.person_data?.onsiteChecklist?.[item.key];
                            const saving = savingKeys.has(`${p._id}_${item.key}`);
                            return (
                              <span
                                key={item.key}
                                className="onsite-list__checklist-icon"
                                data-tooltip={item.label}
                                onClick={(e) => handleIconToggle(e, p, item.key)}
                                style={{ cursor: saving ? 'wait' : undefined, opacity: saving ? 0.5 : 1 }}
                              >
                                <FontAwesomeIcon icon={item.icon} style={{ color: done ? '#2e7d32' : '#b32424', fontSize: 14 }} />
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <button className="onsite-list__register-btn" onClick={() => openForm(p)} data-tooltip="Register">
                          <i className="fa-solid fa-clipboard-check" />
                        </button>
                        <button className="onsite-list__checklist-btn" onClick={() => setChecklist({ show: true, participant: p })} data-tooltip="Checklist">
                          <i className="fa-solid fa-list-check" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="onsite-list__pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: 13, color: '#666' }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: 13 }}
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: 13, color: '#666' }}>— {total} total</span>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              {pageNumbers.slice(0, 5).map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}
              <button
                className="pagination-btn"
                disabled={currentPage === pages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      </div>

      <OnsiteChecklistModal
        show={checklist.show}
        onHide={() => setChecklist({ show: false, participant: null })}
        participant={checklist.participant}
        type={type}
        onChecklistUpdated={handleChecklistUpdated}
      />
      <OnsiteSignatureModal
        show={sigModal.show}
        onHide={() => setSigModal({ show: false, participant: null })}
        participant={sigModal.participant}
        onCleared={handleSignatureCleared}
      />
    </div>
  );
};

export default OnsiteRegistration;
